import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import {
  useArchiveCategory,
  useCategories,
  useCreateCategory,
  type Category,
  type CategoryKind,
} from "@/api/categories";
import {
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  PageTitle,
  Panel,
  Section,
  buttonClass,
  inputClass,
  quietButtonClass,
  rowsClass,
} from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useT } from "@/i18n/LocaleProvider";

/**
 * The category tree.
 *
 * Two levels and no more (`model.md` §3.4), which is why the parent picker only
 * ever offers top-level categories: a third level is refused by the service and
 * by the schema, and offering it here would be inviting a 422.
 *
 * Income and expense are kept in separate groups, because a category tree read
 * top to bottom is a household reading "where does it go" and "where does it
 * come from" as two different questions, not one long list.
 *
 * Archiving is offered; deleting is not. `model.md` §6.6 is archive-never-delete,
 * because historical splits reference the row and a report whose category
 * vanished would silently change. The API still deletes a category nothing has
 * ever used, but a list screen is the wrong place to offer an operation that
 * usually fails.
 */
export function CategoriesPage() {
  const t = useT();
  const { householdId = "" } = useParams();
  const [showArchived, setShowArchived] = useState(false);

  const { data, isLoading, error } = useCategories(householdId, showArchived);
  const create = useCreateCategory(householdId);
  const archive = useArchiveCategory(householdId);

  const [name, setName] = useState("");
  const [kind, setKind] = useState<CategoryKind>("expense");
  const [parentId, setParentId] = useState("");

  // The category *names* this page lists are not translated and must not be:
  // they are the household's own data, seeded in its locale when it was created
  // (K-13) and editable since. Only the interface around them changes language.
  const tree = data?.items ?? [];
  const expenseParents = tree.filter((parent) => parent.kind !== "income");
  const incomeParents = tree.filter((parent) => parent.kind === "income");
  const violation = violationsOf(t, create.error);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      {
        name,
        // A subcategory inherits its parent's tree, so the kind is sent only
        // when there is no parent to inherit from.
        ...(parentId ? { parent_id: parentId } : { kind }),
      },
      { onSuccess: () => setName("") },
    );
  }

  function toggle(id: string, archived: boolean) {
    archive.mutate({ id, archived: !archived });
  }

  return (
    <div>
      <PageTitle>{t("nav.categories")}</PageTitle>

      <Panel className="mt-6 max-w-md">
        <form onSubmit={onSubmit} noValidate>
          <p className="text-sm font-medium text-ink">{t("categories.add")}</p>

          <Field label={t("account.name")} htmlFor="category-name" error={violation("name")}>
            <input
              id="category-name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("categories.namePlaceholder")}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            />
          </Field>

          <Field
            label={t("categories.parent")}
            htmlFor="category-parent"
            hint={t("categories.parentHint")}
            error={violation("parent_id")}
          >
            <select
              id="category-parent"
              name="parent_id"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            >
              <option value="">{t("categories.noParent")}</option>
              {tree.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parent.name}
                </option>
              ))}
            </select>
          </Field>

          {!parentId && (
            <Field label={t("account.kind")} htmlFor="category-kind" error={violation("kind")}>
              <select
                id="category-kind"
                name="kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as CategoryKind)}
                className={`${inputClass} min-h-11 text-base sm:text-sm`}
              >
                <option value="expense">{t("categories.expense")}</option>
                <option value="income">{t("categories.income")}</option>
              </select>
            </Field>
          )}

          <button type="submit" disabled={create.isPending} className={`${buttonClass} mt-4 min-h-11`}>
            {create.isPending ? t("onboarding.creating") : t("common.create")}
          </button>
          <ErrorNote error={create.error} />
        </form>
      </Panel>

      <label className="mt-6 flex min-h-11 items-center gap-2 text-sm text-muted">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        {t("categories.showArchived")}
      </label>

      {isLoading && <Loading />}
      <ErrorNote error={error} />
      <ErrorNote error={archive.error} />

      {!isLoading && tree.length === 0 && (
        <EmptyState title={t("categories.emptyTitle")}>{t("categories.emptyBody")}</EmptyState>
      )}

      {expenseParents.length > 0 && (
        <Section title={t("categories.expense")}>
          <CategoryGroup parents={expenseParents} pending={archive.isPending} onToggle={toggle} />
        </Section>
      )}

      {incomeParents.length > 0 && (
        <Section title={t("categories.income")}>
          <CategoryGroup parents={incomeParents} pending={archive.isPending} onToggle={toggle} />
        </Section>
      )}
    </div>
  );
}

function CategoryGroup({
  parents,
  pending,
  onToggle,
}: {
  parents: Category[];
  pending: boolean;
  onToggle: (id: string, archived: boolean) => void;
}) {
  return (
    <ul className={rowsClass}>
      {parents.map((parent) => (
        <li key={parent.id} className="py-3">
          <CategoryRow
            name={parent.name}
            archived={parent.is_archived}
            pending={pending}
            onToggle={() => onToggle(parent.id, parent.is_archived)}
          />
          {(parent.children ?? []).length > 0 && (
            <ul className="mt-2 space-y-2 border-l border-line pl-4">
              {parent.children?.map((child) => (
                <li key={child.id}>
                  <CategoryRow
                    name={child.name}
                    archived={child.is_archived}
                    pending={pending}
                    onToggle={() => onToggle(child.id, child.is_archived)}
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

function CategoryRow({
  name,
  archived,
  pending,
  onToggle,
}: {
  name: string;
  archived: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  return (
    <div className="flex items-center justify-between gap-4">
      <span className={archived ? "text-muted line-through" : "text-ink"}>
        {name}
        {archived && <span className="ml-2 text-xs text-muted">{t("categories.archived")}</span>}
      </span>
      <button
        type="button"
        onClick={onToggle}
        disabled={pending}
        className={`${quietButtonClass} min-h-11`}
      >
        {archived ? t("categories.unarchive") : t("categories.archive")}
      </button>
    </div>
  );
}
